import React from 'react'
import { css } from '@emotion/core'
import { Link } from '@fs/zion-router'
import {
  colors,
  Divider,
  Expander,
  ExpanderContent,
  ExpanderHeader,
  H2,
  H3,
  LayoutBand,
  Paragraph,
  Separator,
} from '@fs/zion-ui'
import { Trans, useTranslation } from 'react-i18next'

// Zion's ExpanderHeader only tints the background on keyboard focus (~1.2:1), below the 3:1 WCAG 1.4.11 needs
// ExpanderHeader rejects css/className, so target its button from the wrapper
const focusRingCss = css`
  button[aria-expanded]:focus-visible {
    outline: 3px solid currentColor !important;
    outline-offset: 2px;
  }
`

const questions = (t) => [
  {
    key: 'question1',
    question: t('apac.nlihp.faq.q1', 'Is FamilySearch really free?'),
    answer: t(
      'apac.nlihp.faq.a1',
      "Yes. A FamilySearch account is completely free. FamilySearch is an international nonprofit organization that focuses on helping everyone find family stories. Many volunteers and generous donors support the FamilySearch mission of preserving the world's genealogy records."
    ),
  },
  {
    key: 'question2',
    question: t('apac.nlihp.faq.q2', 'Will my information be used for religious purposes?'),
    answer: (
      <Trans i18nKey="apac.nlihp.faq.a2" components={[<Link external wayfinding to="/legal/privacy" />]}>
        {
          'The FamilySearch website is provided free of charge to everyone, regardless of tradition, culture, or religious affiliation. Information you add about living people, including yourself, is kept private and is not shared without your permission. For more information, see the FamilySearch privacy notice at <0>https://www.familysearch.org/en/legal/privacy</0>.'
        }
      </Trans>
    ),
  },
  {
    key: 'question3',
    question: t('apac.nlihp.faq.q3', 'How does FamilySearch benefit from me using their service?'),
    answer: t(
      'apac.nlihp.faq.a3',
      "FamilySearch is the world's largest free genealogy resource. FamilySearch resources help millions of people around the world discover their heritage and connect with family members. Your participation expands those resources and helps others discover their own family connections."
    ),
  },
  {
    key: 'question4',
    question: t('apac.nlihp.faq.q4', 'For what purpose and how does Family Search store my personal information?'),
    answer: t(
      'apac.nlihp.faq.a4',
      'Your personal information or family information will not be sold to other companies. Information about living family members is kept private. The genealogy of deceased ancestors is shared with others who share the community family lineage.'
    ),
  },
]

export default function FAQSection({ image, color }) {
  const { t } = useTranslation()

  return (
    <LayoutBand image={image} color={color || colors.gray02} marginY="xxl" maxWidth="sm">
      <H2 size="H3" centered supplementary>
        {t('apac.nlihp.faq.heading', 'Frequently Asked Questions')}
      </H2>
      <Separator size="xl" />
      {questions(t).map((faq) => (
        <div key={faq.key} css={focusRingCss}>
          <Expander>
            <ExpanderHeader marginY="sm">
              <H3 size="H5">{faq.question}</H3>
            </ExpanderHeader>
            <ExpanderContent>
              <Paragraph>{faq.answer}</Paragraph>
              <Separator size="xs" />
            </ExpanderContent>
          </Expander>
          <Divider />
        </div>
      ))}
    </LayoutBand>
  )
}
